import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Observable, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ModalService } from '../../services/modal.service';
import { Page } from '../../models/page.model';
import { Role } from '../../models/role.model';
import { User } from '../../models/user.model';
import { ArrayJoinPipe } from '../../pipes/arrayJoin.pipe';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../pipes/extractAttribute.pipe';
import { ToLabelPipe } from '../../pipes/toLabel.pipe';
import { KeycloakService } from '../../services/keycloak.service';
import { RoleService } from '../../services/role.service';
import { UserService } from '../../services/user.service';
import { Organisation } from '../../models/organisation.model';
import { OrganisationService } from '../../services/organisation.service';
import { ConfirmDeleteUserDialogComponent, ConfirmDeleteUserDialogContext } from './confirm-delete-dialog.component';
import { UserEditComponent } from './user-edit.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PaginationModule } from 'ngx-bootstrap/pagination';

@Component({
  standalone: true,
  selector: 'users-cmp',
  templateUrl: 'users.component.html',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, UserEditComponent, ArrayJoinPipe, ExtractAttributePipe, CapitalizePipe, ToLabelPipe, TranslatePipe, CommonModule, FormsModule, PaginationModule],
  providers: [UserService, OrganisationService],
})
export class UserComponent implements OnInit, AfterViewInit {
  users: User[];
  error: any;

  @ViewChild('filter') filterElement: ElementRef;
  @ViewChild('filterOrga') filterOrgaElement: ElementRef;
  @ViewChild('termAdvisorFilter') termAdvisorFilter: ElementRef;

  public rows: Array<any> = [];

  public page: number = 1;
  public itemsPerPage: number = 25;
  public maxSize: number = 5;
  public numPages: number = 1;
  public length: number = 0;
  protected isAdmin = false;

  public filter: string;
  public filterOrga: string;
  public organisations: Organisation[] = [];

  public showOrgaDropdown: boolean = false;
  public filteredOrganisations: Organisation[] = [];

  public advisorFilter: string = null;

  public showRef: boolean;
  public isFarmer: boolean;
  public isNegoce: boolean;
  public isManager: boolean;

  private rolesDict: Map<string, Role>;

  static filterToApply: string;
  static filterOrgaToApply: string;
  static filterAdvisorToApply: string;

  constructor(
    private userService: UserService,
    private roleService: RoleService,
    private organisationService: OrganisationService,
    private router: Router,
    private modal: ModalService,
  ) {
    this.isFarmer = KeycloakService.auth.authz.hasRealmRole('ROLE_FARMER');
    this.isManager = KeycloakService.auth.authz.hasRealmRole('ROLE_MANAGER');
    this.showRef =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
  }

  getUsersPaginated(
    pageIndex: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: any,
    filterOrga: string,
  ) {
    this.userService
      .getUsersAdvanced(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter, filterOrga)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<User>) {
    this.rows = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;
  }

  addUser() {
    this.router.navigate(['/users', 'new']);
  }

  deleteUser(user: User, event: any) {
    const context = new ConfirmDeleteUserDialogContext();
    context.showClose = true;
    context.isBlocking = true;
    context.size = 'lg';
    context.user = user;
    context.parent = this;
    this.modal.open(ConfirmDeleteUserDialogComponent, context);
  }

  editUser(user: User, event: any) {
    if (this.isUserMe(user)) {
      this.router.navigate(['/users', 'edit', 'me']);
    } else {
      this.router.navigate(['/users', 'edit', user.id]);
    }
  }

  isUserMe(user: User): boolean {
    // Keycloak stocke le login en minuscule
    return KeycloakService.auth.authz.tokenParsed.preferred_username === user.login.toLowerCase();
  }

  ngOnInit() {
    if (this.isAdmin || this.isNegoce) {
      this.organisationService.getOrganisations().subscribe((orgs) => {
        this.organisations = orgs.sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.filteredOrganisations = this.organisations.slice(); // Copia del array original
      });
    }
  }

  updateUIWithFilters() {
    if (UserComponent.filterToApply) {
      this.filter = UserComponent.filterToApply;
      this.filterElement.nativeElement.value = this.filter;
    }
    if (UserComponent.filterOrgaToApply && this.filterOrgaElement) {
      this.filterOrga = UserComponent.filterOrgaToApply;
      this.filterOrgaElement.nativeElement.value = this.filterOrga;
    }
    if (UserComponent.filterAdvisorToApply && this.termAdvisorFilter) {
      this.advisorFilter = UserComponent.filterAdvisorToApply;
      this.termAdvisorFilter.nativeElement.value = this.advisorFilter;
    }
  }

  ngAfterViewInit() {
    const observables = new Array<Observable<{}>>();
    fromEvent(this.filterElement.nativeElement, 'keyup')
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((term: any) => {
        this.filter = <string>term.target.value;
        this.updateFilters();
      });
    if (this.isAdmin || this.isNegoce) {
      fromEvent(this.filterOrgaElement.nativeElement, 'input')
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((term: any) => {
          this.filterOrga = <string>term.target.value;
          this.updateOrgaFilters();
        });
    }
    if (this.isAdmin || this.isNegoce || this.isManager) {
      fromEvent(this.termAdvisorFilter.nativeElement, 'keyup')
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term: any) => {
          this.advisorFilter = <string>term.target.value;
          this.updateFilters();
        });
    }
    this.onChangeTable();
    this.getRoles();
    this.updateUIWithFilters();
  }

  updateFilters() {
    UserComponent.filterToApply = this.filter;
    if (this.isAdmin || this.isNegoce || this.isManager) {
      UserComponent.filterAdvisorToApply = this.advisorFilter;
    }
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  updateOrgaFilters() {
    UserComponent.filterOrgaToApply = this.filterOrga;
    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    const filter = {
      filter: UserComponent.filterToApply,
      filterAdvisor: UserComponent.filterAdvisorToApply,
    };
    this.getUsersPaginated(pageNumber, 'id', 'desc', filter, UserComponent.filterOrgaToApply);
  }

  getRoles() {
    this.roleService.getRoles().subscribe((roles) => {
      this.rolesDict = new Map<string, Role>();
      for (const role of roles) {
        this.rolesDict.set(role.code, role);
      }
    });
  }

  onOrgaInputChange(event: any) {
    const value = event.target.value.toLowerCase();
    this.filterOrga = value;

    if (value) {
      this.filteredOrganisations = this.organisations.filter((org) => org.name.toLowerCase().includes(value));
    } else {
      this.filteredOrganisations = this.organisations;
    }

    this.showOrgaDropdown = this.filteredOrganisations.length > 0;

    this.updateOrgaFilters();
  }

  selectOrganisation(orgaName: string) {
    this.filterOrga = orgaName;
    this.filterOrgaElement.nativeElement.value = orgaName;
    this.showOrgaDropdown = false;
    this.updateOrgaFilters();
  }

  hideOrgaDropdown() {
    setTimeout(() => {
      this.showOrgaDropdown = false;
    }, 200);
  }
}
